/**
 * pitchShifter — granular pitch shift via two crossfaded delay lines.
 *
 * Web Audio doesn't ship a pitch-shift node, but the classic
 * "Doppler delay" trick gets us 90% of the way there with stock
 * primitives:
 *
 *   - Two parallel DelayNodes share the input.
 *   - Each delay's `delayTime` ramps linearly across a grain window.
 *     A negative slope (delay shrinking) makes the read pointer
 *     advance faster than real time → output is pitched UP. Positive
 *     slope → pitched DOWN.
 *   - The two grains are offset by half a grain so a triangular
 *     gain envelope on each crossfades them together — the
 *     discontinuity at grain boundaries is hidden inside the fade.
 *
 * Math: if delayTime changes at rate `r` seconds per second, output
 * frequency is multiplied by `(1 - r)`. So for a target ratio R:
 *
 *     r = 1 - R
 *     |D_end - D_start| = |r| * grainSec
 *
 * For R > 1 (pitch up) we start at the high end of the buffer and
 * count down to zero; for R < 1 we start at zero and count up. ±24
 * semitones translates to R ∈ [0.25, 4], so the largest required
 * buffer span is 3 × grainSec. We pad to 0.5 s to be safe.
 *
 * Inevitable artifacts:
 *   - Audible "warble" at large shifts when grain rate gets close to
 *     audio rate. 0.1 s grain keeps this below ~10 Hz which most
 *     listeners hear as character, not error.
 *   - Transients can smear since each grain holds the previous slice.
 *     For a delay-feedback path that's fine — we *want* the tail to
 *     bleed into a wash.
 *
 * When pitch is 0 the shifter routes through a bypass gain and the
 * scheduling loop suspends, so a non-shimmering delay costs zero.
 */

const GRAIN_SEC = 0.1;
/** Buffer ceiling — covers ±24 semitones (3× GRAIN_SEC) plus headroom. */
const MAX_BUFFER_SEC = 0.5;
/** Lookahead horizon for the scheduler — we stage grains a bit
 *  ahead of `currentTime` so a momentary JS stall doesn't drop one. */
const LOOKAHEAD_SEC = 0.05;
/** How often the scheduler wakes up to post upcoming grains. */
const SCHEDULER_MS = 25;
/** Crossfade smoothing for the dry / wet mix when toggling pitch
 *  between zero and non-zero. Keeps the bypass switch click-free. */
const MIX_SMOOTHING_SEC = 0.02;

export interface PitchShifter {
  /** Connect upstream signal here. */
  readonly input: GainNode;
  /** Take the shifted signal from here. */
  readonly output: GainNode;
  /** Set pitch shift in semitones, clamped to [-24, +24]. 0 = bypass. */
  setPitchSemitones(semitones: number): void;
  dispose(): void;
}

export function createPitchShifter(ctx: AudioContext): PitchShifter {
  const input = ctx.createGain();
  const output = ctx.createGain();

  // Dry + wet mix: bypass routes input → output directly when pitch
  // is zero so the granular pipeline doesn't introduce artifacts on
  // a no-op shifter. Crossfaded via setTargetAtTime so toggling
  // pitch on / off doesn't click.
  const dry = ctx.createGain();
  dry.gain.value = 1;
  input.connect(dry).connect(output);

  const wet = ctx.createGain();
  wet.gain.value = 0;
  wet.connect(output);

  const delays: DelayNode[] = [];
  const gains: GainNode[] = [];
  for (let i = 0; i < 2; i++) {
    const d = ctx.createDelay(MAX_BUFFER_SEC);
    const g = ctx.createGain();
    g.gain.value = 0;
    input.connect(d);
    d.connect(g);
    g.connect(wet);
    delays.push(d);
    gains.push(g);
  }

  let ratio = 1;
  let nextGrainTime = 0;
  let grainToggle = 0;
  let scheduler: ReturnType<typeof setInterval> | null = null;

  /**
   * Drop the next batch of grains onto the audio clock. Each call
   * walks `nextGrainTime` forward by half-grain steps until we're
   * staged LOOKAHEAD_SEC ahead of `currentTime`. Two delay lines
   * alternate (toggle % 2) so consecutive grains land on opposing
   * sides and crossfade.
   */
  const scheduleGrains = (): void => {
    if (ratio === 1) return;
    const now = ctx.currentTime;
    if (nextGrainTime < now) nextGrainTime = now + 0.005;
    // Slope = 1 - R. Distance traversed across the grain = |slope| × grainSec.
    const slope = 1 - ratio;
    const span = Math.abs(slope) * GRAIN_SEC;
    while (nextGrainTime < now + LOOKAHEAD_SEC) {
      const idx = grainToggle % 2;
      const d = delays[idx]!;
      const g = gains[idx]!;
      const t = nextGrainTime;
      // Pitch up (R > 1, slope < 0): delay decreases from `span` → 0.
      // Pitch down (R < 1, slope > 0): delay increases from 0 → `span`.
      const dStart = slope < 0 ? span : 0;
      const dEnd = slope < 0 ? 0 : span;
      d.delayTime.cancelScheduledValues(t);
      d.delayTime.setValueAtTime(dStart, t);
      d.delayTime.linearRampToValueAtTime(dEnd, t + GRAIN_SEC);
      // Triangle envelope: 0 → 1 → 0, peaking mid-grain.
      g.gain.cancelScheduledValues(t);
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(1, t + GRAIN_SEC / 2);
      g.gain.linearRampToValueAtTime(0, t + GRAIN_SEC);
      nextGrainTime += GRAIN_SEC / 2;
      grainToggle++;
    }
  };

  const startScheduler = (): void => {
    if (scheduler) return;
    nextGrainTime = ctx.currentTime + 0.005;
    grainToggle = 0;
    scheduleGrains();
    scheduler = setInterval(scheduleGrains, SCHEDULER_MS);
  };

  const stopScheduler = (): void => {
    if (scheduler) {
      clearInterval(scheduler);
      scheduler = null;
    }
    // Cancel any pending ramps so the delay nodes settle silently
    // when pitch returns to zero.
    const t = ctx.currentTime;
    for (const d of delays) {
      d.delayTime.cancelScheduledValues(t);
      d.delayTime.setValueAtTime(0, t);
    }
    for (const g of gains) {
      g.gain.cancelScheduledValues(t);
      g.gain.setValueAtTime(0, t);
    }
  };

  const setPitchSemitones = (semitones: number): void => {
    const clamped = Math.max(-24, Math.min(24, semitones));
    const newRatio = clamped === 0 ? 1 : Math.pow(2, clamped / 12);
    ratio = newRatio;
    const t = ctx.currentTime;
    if (clamped === 0) {
      // Crossfade back to bypass.
      dry.gain.cancelScheduledValues(t);
      dry.gain.setTargetAtTime(1, t, MIX_SMOOTHING_SEC);
      wet.gain.cancelScheduledValues(t);
      wet.gain.setTargetAtTime(0, t, MIX_SMOOTHING_SEC);
      stopScheduler();
    } else {
      dry.gain.cancelScheduledValues(t);
      dry.gain.setTargetAtTime(0, t, MIX_SMOOTHING_SEC);
      wet.gain.cancelScheduledValues(t);
      wet.gain.setTargetAtTime(1, t, MIX_SMOOTHING_SEC);
      startScheduler();
    }
  };

  const dispose = (): void => {
    stopScheduler();
    try {
      input.disconnect();
    } catch {
      /* already disconnected */
    }
    try {
      output.disconnect();
    } catch {
      /* already disconnected */
    }
    for (const d of delays) {
      try {
        d.disconnect();
      } catch {
        /* already disconnected */
      }
    }
    for (const g of gains) {
      try {
        g.disconnect();
      } catch {
        /* already disconnected */
      }
    }
    try {
      dry.disconnect();
    } catch {
      /* already disconnected */
    }
    try {
      wet.disconnect();
    } catch {
      /* already disconnected */
    }
  };

  return { input, output, setPitchSemitones, dispose };
}
